import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  InputLabel,
} from '@mui/material';
import { Close as CloseIcon, CloudUpload as CloudUploadIcon, Info as InfoIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { createNotification } from '../../services/notificationService';
import { uploadFile } from '../../firebase';

interface AddNotificationDialogProps {
  open: boolean;
  onClose: (created?: boolean) => void;
}

const AddNotificationDialog: React.FC<AddNotificationDialogProps> = ({ open, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    img: '',
    recipients: '',
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleImageFile(file);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageFile(file);
    }
  };

  const handleImageFile = useCallback((file: File) => {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Formato de archivo no válido. Use JPG, PNG o WebP.');
      return;
    }

    // Validate file size (1MB max)
    if (file.size > 1024 * 1024) {
      setError('El archivo es demasiado grande. El tamaño máximo permitido es 1MB.');
      return;
    }

    // Validate image dimensions (1:1 aspect ratio)
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    
    img.onload = () => {
      if (img.width !== img.height) {
        setError('La imagen debe tener una relación de aspecto 1:1 (ancho = alto)');
        URL.revokeObjectURL(objectUrl);
      } else {
        setError('');
        setImageFile(file);
        setFormData(prev => ({ ...prev, img: objectUrl }));
        setImagePreview(objectUrl);
      }
    };
    
    img.onerror = () => {
      setError('Error al cargar la imagen');
      URL.revokeObjectURL(objectUrl);
    };
    
    img.src = objectUrl;
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.description.trim()) {
      setError('Por favor complete todos los campos obligatorios');
      return;
    }

    if (!imageFile) {
      setError('Por favor seleccione una imagen');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      
      // Upload image to Firebase Storage in /notifications path
      const timestamp = Date.now();
      const fileExtension = imageFile.name.split('.').pop();
      const fileName = `notification_${timestamp}.${fileExtension}`;
      const storagePath = `notifications/${fileName}`;
      
      enqueueSnackbar('Subiendo imagen...', { variant: 'info' });
      const imageUrl = await uploadFile(imageFile, storagePath);
      
      // Create the notification data with the Firebase Storage URL
      const notificationData = {
        title: formData.title,
        description: formData.description,
        img: imageUrl,
        recipients: formData.recipients || 'all'
      };
      
      await createNotification(notificationData);
      enqueueSnackbar('Notificación creada exitosamente', { variant: 'success' });
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        recipients: '',
        img: ''
      });
      setImageFile(null);
      setImagePreview('');
      
      onClose(true);
      
    } catch (error: any) {
      console.error('Error creating notification:', error);
      setError(error.response?.data?.message || 'Error al crear la notificación. Por favor, intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Nueva Notificación</Typography>
          <IconButton onClick={() => onClose()} disabled={isLoading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box mb={3}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
              <Box flex={1}>
                <TextField
                  name="title"
                  label="Título"
                  value={formData.title}
                  onChange={handleChange}
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  required
                />
                <TextField
                  name="description"
                  label="Descripción"
                  value={formData.description}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  margin="normal"
                  required
                />
                <TextField
                  name="recipients"
                  label="Destinatarios (opcional, separados por comas)"
                  value={formData.recipients}
                  onChange={handleChange}
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  helperText={
                    <Box component="span" display="flex" alignItems="center" gap={0.5} color="error.main" fontWeight="bold">
                      <InfoIcon fontSize="small" />
                      Dejar vacío para enviar a todos los Consumidores
                    </Box>
                  }
                />
              </Box>

              <Box width={{ xs: '100%', md: 300 }} display="flex" flexDirection="column" alignItems="center">
                <InputLabel sx={{ mb: 1, fontWeight: 500 }}>Imagen de la notificación *</InputLabel>
                <Box
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('file-upload')?.click()}
                  sx={{
                    border: '2px dashed',
                    borderColor: isDragging ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: isDragging ? 'action.hover' : 'background.paper',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover',
                    },
                    width: '100%',
                    aspectRatio: '1/1',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    maxHeight: 300
                  }}
                >
                  <input
                    id="file-upload"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                  {!imagePreview ? (
                    <>
                      <CloudUploadIcon 
                        color={isDragging ? 'primary' : 'action'} 
                        sx={{ fontSize: 48, mb: 1, mx: 'auto' }} 
                      />
                      <Typography variant="body2" color="textPrimary" gutterBottom>
                        {isDragging ? 'Suelta la imagen aquí' : 'Haz clic para seleccionar o arrastra una imagen'}
                      </Typography>
                      <Box mt={1}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Formato: Cuadrada (1:1)
                        </Typography>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Formatos: WebP, PNG, JPG
                        </Typography>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Tamaño máximo: 1MB
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <Box>
                      <Box
                        component="img"
                        src={imagePreview}
                        alt="Vista previa"
                        sx={{
                          maxWidth: '100%',
                          maxHeight: 180,
                          objectFit: 'contain',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          mx: 'auto',
                          display: 'block'
                        }}
                      />
                      <Button
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          setImagePreview('');
                          setImageFile(null);
                          setFormData(prev => ({ ...prev, img: '' }));
                        }}
                        sx={{ mt: 1 }}
                      >
                        Cambiar imagen
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
          <DialogActions>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={isLoading || !formData.title || !formData.description || !imageFile}
              startIcon={isLoading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
              sx={{
                '&:hover': {
                  backgroundColor: '#C8102E',
                },
              }}
            >
              {isLoading ? 'Creando...' : 'Crear Notificación'}
            </Button>
          </DialogActions>
        </DialogContent>
      </form>
    </Dialog>
  );
};

export default AddNotificationDialog;
